/** Default homepage CMS content — Harvyq ERP by Boldcomp Solutions */
export const HOMEPAGE_DEFAULTS = {
  brand: {
    productName: 'Harvyq',
    parentCompany: 'Boldcomp Solutions',
    tagline: 'Enterprise Resource Planning',
    logoUrl: '',
    logoAlt: 'Harvyq by Boldcomp Solutions'
  },
  hero: {
    headline: 'The Complete ERP System for Kenyan Businesses',
    subheadline:
      'Harvyq by Boldcomp Solutions unifies sales, inventory, finance, customers, suppliers, and multi-branch operations in one platform.',
    primaryCta: 'Start Free Trial',
    primaryCtaLink: '/register',
    secondaryCta: 'Watch Demo',
    secondaryCtaLink: '#demo',
    imageUrl: '/images/pos-preview.png',
    stats: [
      { number: '500+', label: 'Businesses' },
      { number: '50K+', label: 'Transactions' },
      { number: '99.9%', label: 'Uptime' }
    ]
  },
  demo: {
    enabled: true,
    sectionTitle: 'See Harvyq ERP in Action',
    sectionSubtitle: 'Watch how teams run their entire business on Harvyq',
    videos: [
      {
        title: 'Harvyq ERP Overview',
        url: '',
        description: 'A walkthrough of core ERP modules — POS, inventory, finance, and reporting.'
      }
    ]
  },
  features: {
    sectionTitle: 'Everything You Need to Run Your Business',
    sectionSubtitle: 'A full ERP suite built for Kenyan SMEs — not just a cash register',
    items: [
      {
        icon: '🛒',
        title: 'Point of Sale',
        description: 'Fast POS with barcode scanning, receipts, and multi-tender payments'
      },
      {
        icon: '📦',
        title: 'Inventory & Procurement',
        description: 'Stock control, suppliers, purchase flow, and low-stock alerts'
      },
      {
        icon: '👥',
        title: 'CRM & Customers',
        description: 'Customer profiles, credit sales, and purchase history'
      },
      {
        icon: '💰',
        title: 'Finance & Debt Tracking',
        description: 'Sell on credit, track balances, record repayments, and cashflow'
      },
      {
        icon: '🏢',
        title: 'Multi-Branch ERP',
        description: 'Run multiple outlets with centralized control and reporting'
      },
      {
        icon: '📊',
        title: 'Analytics & Reports',
        description: 'Sales, inventory, and financial intelligence for better decisions'
      }
    ]
  },
  pricing: {
    sectionTitle: 'Simple, Transparent Pricing',
    sectionSubtitle: 'Choose the ERP plan that fits your business',
    plans: [
      {
        name: 'Starter',
        price: '1,999',
        period: 'month',
        features: [
          'Up to 2 users',
          'Up to 500 products',
          'POS & inventory',
          'Sales reports',
          'Email support'
        ],
        cta: 'Start Free Trial',
        popular: false
      },
      {
        name: 'Professional',
        price: '3,999',
        period: 'month',
        features: [
          'Up to 5 users',
          'Up to 2,000 products',
          'Full ERP modules',
          'Customer & debt tracking',
          'Supplier management',
          'Advanced reports',
          'Priority support'
        ],
        cta: 'Start Free Trial',
        popular: true
      },
      {
        name: 'Enterprise',
        price: '7,999',
        period: 'month',
        features: [
          'Up to 15 users',
          'Up to 10,000 products',
          'Multi-branch ERP',
          'API access',
          'Dedicated account manager',
          '24/7 phone support',
          'Custom integrations'
        ],
        cta: 'Contact Sales',
        popular: false
      }
    ]
  },
  testimonials: {
    sectionTitle: 'What Our Customers Say',
    sectionSubtitle: 'Trusted by businesses across Kenya',
    items: [
      {
        name: 'Martha Kachelawa',
        business: 'Boldzen Hardware',
        content:
          'Harvyq ERP transformed how we manage inventory and credit. One system for the whole business.',
        rating: 5
      },
      {
        name: 'Shadrack Powon',
        business: 'Suk Hardware',
        content: 'Easy to use, great support, and the reports help me understand my business better.',
        rating: 5
      },
      {
        name: 'Daniel Pkemoi',
        business: 'Kephihub Ventures',
        content: 'Multi-user and multi-branch support is perfect for our team across locations.',
        rating: 5
      }
    ]
  },
  cta: {
    headline: 'Ready to Transform Your Business?',
    subtext: 'Join hundreds of Kenyan businesses running on Harvyq ERP by Boldcomp Solutions',
    buttonText: 'Start Your 14-Day Free Trial',
    buttonLink: '/register',
    footnote: 'No credit card required'
  },
  footer: {
    about:
      'Harvyq is the ERP platform from Boldcomp Solutions — built for Kenyan SMEs to run sales, stock, finance, and operations in one place.',
    phone: '+254 726 642 205',
    email: 'info@harvyq.com',
    address: 'Nairobi, Kenya',
    copyright: '© 2026 Harvyq by Boldcomp Solutions. All rights reserved.',
    social: {
      facebook: '',
      twitter: '',
      instagram: '',
      linkedin: ''
    }
  }
};

export function deepMergeHomepage(saved = {}) {
  return {
    ...HOMEPAGE_DEFAULTS,
    ...saved,
    brand: { ...HOMEPAGE_DEFAULTS.brand, ...(saved.brand || {}) },
    hero: {
      ...HOMEPAGE_DEFAULTS.hero,
      ...(saved.hero || {}),
      stats: saved.hero?.stats?.length ? saved.hero.stats : HOMEPAGE_DEFAULTS.hero.stats
    },
    demo: {
      ...HOMEPAGE_DEFAULTS.demo,
      ...(saved.demo || {}),
      videos: saved.demo?.videos?.length ? saved.demo.videos : HOMEPAGE_DEFAULTS.demo.videos
    },
    features: {
      ...HOMEPAGE_DEFAULTS.features,
      ...(saved.features || {}),
      items: saved.features?.items?.length ? saved.features.items : HOMEPAGE_DEFAULTS.features.items
    },
    pricing: {
      ...HOMEPAGE_DEFAULTS.pricing,
      ...(saved.pricing || {}),
      plans: saved.pricing?.plans?.length ? saved.pricing.plans : HOMEPAGE_DEFAULTS.pricing.plans
    },
    testimonials: {
      ...HOMEPAGE_DEFAULTS.testimonials,
      ...(saved.testimonials || {}),
      items: saved.testimonials?.items?.length
        ? saved.testimonials.items
        : HOMEPAGE_DEFAULTS.testimonials.items
    },
    cta: { ...HOMEPAGE_DEFAULTS.cta, ...(saved.cta || {}) },
    footer: {
      ...HOMEPAGE_DEFAULTS.footer,
      ...(saved.footer || {}),
      social: { ...HOMEPAGE_DEFAULTS.footer.social, ...(saved.footer?.social || {}) }
    }
  };
}

/** Convert YouTube / Vimeo / direct URL to an embeddable iframe src */
export function toEmbedUrl(url) {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (!trimmed) return '';

  const yt =
    trimmed.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/) ||
    trimmed.match(/youtube\.com\/shorts\/([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;

  const vimeo = trimmed.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;

  if (trimmed.includes('embed') || trimmed.endsWith('.mp4')) return trimmed;
  return trimmed;
}

export function resolveMediaUrl(path) {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  return path.startsWith('/') ? path : `/${path}`;
}
